import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import VideoPlayer from './VideoPlayer';
import AnimatedImage from './animated/AnimatedImage';
import BackgroundEffects from './animated/BackgroundEffects';
import AccessRequestModal from './AccessRequestModal';
import { withBrand } from './BrandName';
import { getBadgeAction } from '../data/badgeLinks';
import { gradients, colors, radii, fillImageSx, fadeUp } from '../theme/designTokens';

const PageHero = ({
  backgroundImage,
  sideImage,
  videoSrc,
  eyebrow,
  title,
  titleHighlight,
  subtitle,
  minHeight = { xs: 300, sm: 400, md: 500 },
  align = 'center',
  variant = 'dark',
  badges = ['AERA™', 'Resilience Engineering'],
}) => {
  const isLight = variant === 'light';
  const hasSplit = isLight && sideImage;
  const [accessTopic, setAccessTopic] = useState(null);

  return (
    <BackgroundEffects
      variant={isLight ? 'light' : 'dark'}
      mesh={isLight}
      sx={{
        position: 'relative',
        minHeight: hasSplit ? { xs: 'auto', md: 540 } : minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isLight ? undefined : '#020817',
        background: isLight
          ? gradients.heroLight
          : backgroundImage
            ? 'transparent'
            : gradients.heroBlue,
      }}
    >
      {!isLight && backgroundImage && (
        <Box
          component="img"
          src={backgroundImage}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            ...fillImageSx,
            zIndex: 0,
          }}
        />
      )}

      {!isLight && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: gradients.heroOverlay,
            zIndex: 1,
          }}
        />
      )}

      <Box
        component={motion.div}
        initial="hidden"
        animate="visible"
        sx={{
          position: 'relative',
          zIndex: 2,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 6, md: 8 },
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          display: hasSplit ? 'grid' : 'block',
          gridTemplateColumns: hasSplit ? { xs: '1fr', lg: '1fr 1fr' } : undefined,
          gap: hasSplit ? { xs: 5, lg: 7 } : undefined,
          alignItems: 'center',
        }}
      >
        <Box sx={{ textAlign: hasSplit ? { xs: 'center', lg: align } : align }}>
          {eyebrow && (
            <Typography
              component={motion.p}
              custom={0}
              variants={fadeUp}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: isLight ? colors.slate : colors.ochre,
                mb: 2,
              }}
            >
              {eyebrow}
            </Typography>
          )}

          <Typography
            component={motion.h1}
            custom={1}
            variants={fadeUp}
            sx={{
              fontFamily: '"Cormorant Garamond", "Georgia", serif',
              fontSize: { xs: '2.5rem', sm: '3rem', md: hasSplit ? '3.25rem' : '3.75rem' },
              fontWeight: 500,
              color: isLight ? colors.navy : '#fff',
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              mb: subtitle ? 2.5 : videoSrc ? 3 : 0,
            }}
          >
            {withBrand(title)}{' '}
            {titleHighlight && (
              <Box
                component="span"
                sx={{
                  color: isLight ? colors.navy : colors.ochreLight,
                }}
              >
                {withBrand(titleHighlight)}
              </Box>
            )}
          </Typography>

          {subtitle && (
            <Typography
              component={motion.p}
              custom={2}
              variants={fadeUp}
              sx={{
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                color: isLight ? colors.slate : 'rgba(255,255,255,0.82)',
                maxWidth: 640,
                mx: hasSplit ? { xs: 'auto', lg: align === 'center' ? 'auto' : 0 } : align === 'center' ? 'auto' : 0,
                lineHeight: 1.8,
                mb: videoSrc ? 3 : 0,
              }}
            >
              {withBrand(subtitle)}
            </Typography>
          )}

          {badges?.length > 0 && (
            <Box
              component={motion.div}
              custom={3}
              variants={fadeUp}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: hasSplit
                  ? { xs: 'center', lg: align === 'center' ? 'center' : 'flex-start' }
                  : align === 'center'
                    ? 'center'
                    : 'flex-start',
                mt: 3,
              }}
            >
              {badges.map((badge) => {
                const action = getBadgeAction(badge);
                const isInteractive = action.type !== 'none';
                const badgeSx = {
                  px: 2,
                  py: 0.75,
                  borderRadius: radii.sm,
                  background: isLight ? 'rgba(15,23,42,0.08)' : 'rgba(51,65,85,0.55)',
                  border: `1px solid ${isLight ? colors.navyMid : 'rgba(30,41,59,0.95)'}`,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: isLight ? colors.navy : colors.ochreLight,
                  textDecoration: 'none',
                  display: 'inline-block',
                  fontFamily: 'inherit',
                  transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                  ...(isInteractive
                    ? {
                        cursor: 'pointer',
                        '&:hover': {
                          background: isLight ? 'rgba(15,23,42,0.12)' : 'rgba(71,85,105,0.7)',
                          borderColor: isLight ? colors.navy : colors.navyDark,
                          color: isLight ? colors.navy : colors.ochre,
                        },
                      }
                    : {}),
                };

                if (action.type === 'access') {
                  return (
                    <Box
                      key={badge}
                      component="button"
                      type="button"
                      onClick={() => setAccessTopic(action.topic)}
                      sx={{ ...badgeSx, appearance: 'none' }}
                    >
                      {badge}
                    </Box>
                  );
                }

                if (action.type === 'link') {
                  return (
                    <Box key={badge} component={RouterLink} to={action.to} sx={badgeSx}>
                      {badge}
                    </Box>
                  );
                }

                return (
                  <Box key={badge} component="span" sx={badgeSx}>
                    {badge}
                  </Box>
                );
              })}
            </Box>
          )}

          {videoSrc && (
            <Box
              component={motion.div}
              custom={3}
              variants={fadeUp}
              sx={{
                width: { xs: '100%', sm: '85%', md: '60%', lg: '50%' },
                mx: 'auto',
                borderRadius: radii.lg,
                overflow: 'hidden',
                boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
                border: '2px solid rgba(238,186,43,0.3)',
                mt: 3,
              }}
            >
              <VideoPlayer src={videoSrc} width="100%" />
            </Box>
          )}
        </Box>

        {hasSplit && (
          <Box
            component={motion.div}
            custom={2}
            variants={fadeUp}
            sx={{ position: 'relative', maxWidth: { xs: 420, lg: '100%' }, mx: 'auto', width: '100%' }}
          >
            <AnimatedImage
              src={sideImage}
              alt="ALEgION"
              maxHeight={420}
              objectPosition="center"
              fit="natural"
            />
          </Box>
        )}
      </Box>

      <AccessRequestModal
        open={Boolean(accessTopic)}
        topic={accessTopic || 'Access'}
        onClose={() => setAccessTopic(null)}
      />
    </BackgroundEffects>
  );
};

export default PageHero;
